import React, { useState, useEffect } from 'react'
import {
  ShoppingCartIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'
import { financeiroAPI, paymentGatewayAPI } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import { PaymentDetailsModal } from '@/components/PaymentDetailsModal'

interface Product {
  id: number
  code: string
  name: string
  sale_price: number  // In centavos (ex: 120000 = R$ 1200,00)
  current_stock?: number
  track_inventory: boolean
  unit: string
}

interface SaleItem {
  product_id: number
  product?: Product
  quantity: number
  unit_price?: number  // In centavos
  discount?: number    // In centavos
}

const SalesPage: React.FC = () => {
  const navigate = useNavigate()

  // Form state
  const [descricao, setDescricao] = useState('')
  const [dataVencimento, setDataVencimento] = useState(new Date().toISOString().split('T')[0])
  const [numeroDocumento, setNumeroDocumento] = useState('')
  const [observacoes, setObservacoes] = useState('')

  // Selections
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null)
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const [selectedGatewayId, setSelectedGatewayId] = useState<number | null>(null)

  // Items
  const [items, setItems] = useState<SaleItem[]>([])

  // Data
  const [products, setProducts] = useState<Product[]>([])
  const [sellers, setSellers] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [gateways, setGateways] = useState<any[]>([])

  // UI state
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [showProductModal, setShowProductModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentData, setPaymentData] = useState<any>(null)

  // Load data
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsData, sellersData, customersData, accountsData, categoriesData, gatewaysData] = await Promise.all([
        financeiroAPI.produtos.list({ active_only: true }),
        financeiroAPI.vendedores.list(),
        financeiroAPI.clientes.list(),
        financeiroAPI.contasBancarias.list(),
        financeiroAPI.categorias.list(),
        paymentGatewayAPI.getGateways().catch(() => []) // Gracefully handle if gateways fail
      ])

      setProducts(productsData)
      setSellers(sellersData)
      setCustomers(customersData)
      setAccounts(accountsData)
      setGateways(gatewaysData)

      console.log('Gateways carregados:', gatewaysData)
      console.log('Total de gateways:', gatewaysData?.length || 0)

      const incomeCategories = categoriesData.filter((cat: any) => cat.type === 'INCOME')
      setCategories(incomeCategories)

      if (accountsData.length > 0) setSelectedAccountId(accountsData[0].id)
      if (incomeCategories.length > 0) setSelectedCategoryId(incomeCategories[0].id)

      setLoading(false)
    } catch (err: any) {
      console.error('Error loading data:', err)
      setError('Erro ao carregar dados')
      setLoading(false)
    }
  }

  const addProduct = (product: Product) => {
    const existing = items.find(item => item.product_id === product.id)

    if (existing) {
      setItems(items.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      setItems([...items, {
        product_id: product.id,
        product: product,
        quantity: 1,
        unit_price: product.sale_price,
        discount: 0
      }])
    }

    setShowProductModal(false)
    setProductSearch('')
  }

  const removeProduct = (productId: number) => {
    setItems(items.filter(item => item.product_id !== productId))
  }

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeProduct(productId)
      return
    }
    setItems(items.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ))
  }

  const updatePrice = (productId: number, unit_price_reais: number) => {
    const unit_price = Math.round(unit_price_reais * 100)  // Convert to centavos
    setItems(items.map(item =>
      item.product_id === productId ? { ...item, unit_price } : item
    ))
  }

  const updateDiscount = (productId: number, discount_reais: number) => {
    const discount = Math.round(discount_reais * 100)  // Convert to centavos
    setItems(items.map(item =>
      item.product_id === productId ? { ...item, discount } : item
    ))
  }

  const calculateItemTotal = (item: SaleItem) => {
    const price = item.unit_price || item.product?.sale_price || 0
    const subtotal = item.quantity * price
    const discount = item.discount || 0
    return subtotal - discount
  }

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0)
  }

  const formatCurrency = (centavos: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(centavos / 100)  // Convert centavos to reais for display
  }

  const validateForm = () => {
    if (!descricao.trim()) {
      setError('Descrição é obrigatória')
      return false
    }
    if (!selectedAccountId || !selectedCategoryId) {
      setError('Conta e categoria são obrigatórias')
      return false
    }
    if (items.length === 0) {
      setError('Adicione pelo menos um produto')
      return false
    }

    // Validate stock
    for (const item of items) {
      if (item.product?.track_inventory) {
        const availableStock = item.product.current_stock || 0
        if (item.quantity > availableStock) {
          setError(`Estoque insuficiente para ${item.product.name}. Disponível: ${availableStock}`)
          return false
        }
      }
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setSubmitting(true)
      setError(null)

      const saleData = {
        descricao,
        data_vencimento: dataVencimento,
        conta_id: selectedAccountId,
        categoria_id: selectedCategoryId,
        cliente_id: selectedCustomerId,
        seller_id: selectedSellerId,
        numero_documento: numeroDocumento || undefined,
        observacoes: observacoes || undefined,
        payment_method: selectedPaymentMethod || undefined,
        gateway_id: selectedGatewayId || undefined,
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount || 0
        }))
      }

      const result = await financeiroAPI.vendas.create(saleData)
      setSuccess(true)

      // Verificar se há dados de pagamento na resposta
      if (result.payment_data) {
        console.log('Dados de pagamento recebidos:', result.payment_data)
        setPaymentData({
          payment_link: result.payment_data.payment_link,
          qr_code: result.payment_data.qr_code,
          qr_code_text: result.payment_data.qr_code_text,
          barcode: result.payment_data.barcode,
          gateway_charge_id: result.payment_data.gateway_charge_id,
          status: result.payment_data.status || 'pending',
          payment_method: selectedPaymentMethod,
          amount: calculateTotal()
        })
        setShowPaymentModal(true)
      }

      setTimeout(() => navigate('/financeiro/contas-receber'), 2000)
    } catch (err: any) {
      console.error('Error creating sale:', err)
      setError(err.response?.data?.detail || 'Erro ao criar venda')
      setSubmitting(false)
    }
  }

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.code.toLowerCase().includes(productSearch.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-green-200">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Venda criada com sucesso!</h2>
          <p className="text-gray-600">Redirecionando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Criar Nova Venda</h2>
        <p className="text-gray-600">Preencha os dados e adicione produtos à venda</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-red-900">Erro</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products Section - Larger */}
          <div className="lg:col-span-2 space-y-6">
            {/* Products Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Produtos da Venda</h3>
                <button
                  type="button"
                  onClick={() => setShowProductModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Adicionar
                </button>
              </div>

              <div className="p-4">
                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCartIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Nenhum produto adicionado</p>
                    <p className="text-sm">Clique em "Adicionar" para começar</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.product_id}
                        className="bg-gray-50 rounded-lg p-4 flex items-center gap-4"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.product?.name}</p>
                          <p className="text-sm text-gray-500">Código: {item.product?.code}</p>
                          {item.product?.track_inventory && (
                            <p className={`text-xs ${
                              (item.product.current_stock || 0) >= item.quantity ? 'text-green-600' : 'text-red-600'
                            }`}>
                              Estoque: {item.product.current_stock || 0} {item.product.unit}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <div>
                            <label className="text-xs text-gray-600">Qtd</label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.product_id, parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </div>

                          <div>
                            <label className="text-xs text-gray-600">Preço (R$)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={(item.unit_price || 0) / 100}
                              onChange={(e) => updatePrice(item.product_id, parseFloat(e.target.value) || 0)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>

                          <div>
                            <label className="text-xs text-gray-600">Desc. (R$)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={(item.discount || 0) / 100}
                              onChange={(e) => updateDiscount(item.product_id, parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>

                          <div className="text-right">
                            <p className="text-xs text-gray-600">Total</p>
                            <p className="font-semibold text-blue-600">
                              {formatCurrency(calculateItemTotal(item))}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeProduct(item.product_id)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Total */}
              {items.length > 0 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">Total da Venda:</span>
                    <span className="text-2xl font-bold text-blue-600">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields - Sidebar */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Informações Básicas</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <input
                  type="text"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex: Venda - Cliente XYZ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento *</label>
                <input
                  type="date"
                  value={dataVencimento}
                  onChange={(e) => setDataVencimento(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nº Documento</label>
                <input
                  type="text"
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                  placeholder="NF-001234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Observações adicionais..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Relations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Relacionamentos</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
                <select
                  value={selectedSellerId || ''}
                  onChange={(e) => setSelectedSellerId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Nenhum</option>
                  {sellers.map((seller) => (
                    <option key={seller.id} value={seller.id}>{seller.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                <select
                  value={selectedCustomerId || ''}
                  onChange={(e) => setSelectedCustomerId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Nenhum</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conta *</label>
                <select
                  value={selectedAccountId || ''}
                  onChange={(e) => setSelectedAccountId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>{account.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                <select
                  value={selectedCategoryId || ''}
                  onChange={(e) => setSelectedCategoryId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                <select
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione...</option>
                  <option value="PIX">PIX</option>
                  <option value="CREDIT_CARD">Cartão de Crédito</option>
                  <option value="DEBIT_CARD">Cartão de Débito</option>
                  <option value="BANK_SLIP">Boleto</option>
                  <option value="TRANSFER">Transferência</option>
                  <option value="CASH">Dinheiro</option>
                  <option value="CHECK">Cheque</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gateway de Pagamento</label>
                <select
                  value={selectedGatewayId || ''}
                  onChange={(e) => setSelectedGatewayId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Nenhum</option>
                  {gateways.map((gateway: any) => {
                    const isFinPro = gateway.gateway_type?.toLowerCase() === 'finpro' ||
                                     gateway.name?.toLowerCase().includes('finpro') ||
                                     gateway.name?.toLowerCase().includes('hubnai')
                    const displayName = isFinPro
                      ? `${gateway.name} (Sugerido)`
                      : gateway.name

                    return (
                      <option key={gateway.id} value={gateway.id}>
                        {displayName}
                      </option>
                    )
                  })}
                </select>
                {selectedGatewayId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Gateway selecionado irá processar o pagamento automaticamente
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/financeiro/contas-receber')}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting || items.length === 0}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Criando...' : 'Criar Venda'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Selecionar Produto</h3>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Buscar por nome ou código..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum produto encontrado</p>
              ) : (
                <div className="space-y-2">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => addProduct(product)}
                      className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-500">Código: {product.code}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-blue-600">{formatCurrency(product.sale_price)}</p>
                          {product.track_inventory && (
                            <p className={`text-xs ${(product.current_stock || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              Estoque: {product.current_stock || 0} {product.unit}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Dados de Pagamento */}
      <PaymentDetailsModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentData={paymentData}
        saleDescription={descricao}
      />
    </div>
  )
}

export default SalesPage
